import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useLanguageContext } from "@/contexts/LanguageContext";

export interface ActiveBanner {
  id: string;
  title: string;
  subtitle: string | null;
  imageUrl: string | null;
  imageAlt: string | null;
  backgroundColor: string;
  textColor: string;
  ctaUrl: string | null;
  ctaText: string | null;
  ctaTarget: "_self" | "_blank";
  dismissible: boolean;
}

export function useActiveBanner() {
  const { locale } = useLanguageContext();

  return useQuery({
    queryKey: ["active-banner", locale],
    queryFn: async (): Promise<ActiveBanner | null> => {
      const { data, error } = await supabase
        .from("announcement_banners")
        .select("*")
        .eq("is_active", true)
        .is("archived_at", null)
        .limit(1)
        .maybeSingle();

      if (error) throw error;
      if (!data) return null;

      const isAr = locale === "ar";
      return {
        id: data.id,
        title: isAr ? data.title_ar : data.title_fr,
        subtitle: isAr ? data.subtitle_ar ?? null : data.subtitle_fr ?? null,
        imageUrl: data.image_url ?? null,
        imageAlt: isAr ? data.image_alt_ar ?? null : data.image_alt_fr ?? null,
        backgroundColor: data.background_color,
        textColor: data.text_color,
        ctaUrl: data.cta_url ?? null,
        ctaText: isAr ? data.cta_text_ar ?? null : data.cta_text_fr ?? null,
        ctaTarget: (data.cta_target as "_self" | "_blank") ?? "_self",
        dismissible: data.dismissible,
      };
    },
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });
}
